import FileUpload from "components/FileUpload";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import {
  addPrivateProsecution,
  editPrivateProsecution,
} from "services/privateProsecutionService";
import { InvestigationFile, PrivateProsecution } from "types";
import { LoadingIcon } from "utils/constant";
import {
  errorHandlerOnForm,
  formatPhoneNumber,
  isEmailCorrect,
  isPhoneCorrect,
} from "utils/helper";

interface Props {
  file?: InvestigationFile;
  privateProsecution?: PrivateProsecution;
  setRefetch: () => void;
  onCancel: () => void;
}

const RequestPrivateProsecutiion: React.FC<Props> = ({
  file,
  privateProsecution,
  onCancel,
  setRefetch,
}) => {
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);

  const [formState, setFormState] = useState(
    privateProsecution
      ? privateProsecution
      : {
          applicant_name: "",
          applicant_contact: "",
          applicant_region: "",
          applicant_city: "",
          applicant_sub_city: "",
          applicant_woreda: "",
          applicant_kebele: "",
          applicant_house_number: "",
          applicant_phone_number: "",
          applicant_email: "",
          reason_for_private_prosecution: "",
          registaration_number: "", // note: typo preserved from interface
          registration_date: new Date().toISOString().split("T")[0],
          status: "",
          investigation_file: file?.id || null,
          private_prosecution_decision_file: null,
        },
  );

  useEffect(() => {
    if (privateProsecution) {
      setFormState(privateProsecution);
    }
  }, [privateProsecution]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState.applicant_name) return "Applicant name is required.";
    if (
      formState.applicant_phone_number &&
      !isPhoneCorrect(formState?.applicant_phone_number)
    )
      return "Please Enter a Correct Phone Number.";
    if (formState.applicant_email && !isEmailCorrect(formState.applicant_email))
      return "Please Enter a Valid Email Address.";
    if (!formState.reason_for_private_prosecution)
      return "Reason for private prosecution is required.";
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    // Convert date string to Date object if needed
    const payload = {
      ...formState,
      status: "pending",
      forwarding_status: "on_prosecutor_desk",
      investigation_file: file?.id,
    };

    const api = privateProsecution
      ? editPrivateProsecution
      : addPrivateProsecution;

    api(payload)
      .then(() => {
        showTooltip("Private prosecution submitted successfully!", "success");
        setRefetch();
        onCancel();
      })
      .catch((err) => {
        showTooltip(errorHandlerOnForm(err?.response?.data), "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onCancel();
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-800">
          Private Prosecution Request
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4 mt-4">
            {/* Applicant Information */}
            <div className="col-span-2">
              <h3 className="font-medium text-gray-700 mb-2">
                Applicant Information
              </h3>
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Applicant Name *
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="applicant_name"
                value={formState.applicant_name}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Applicant Contact
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="applicant_contact"
                value={formState.applicant_contact}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Region
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="applicant_region"
                value={formState.applicant_region}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                City
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="applicant_city"
                value={formState.applicant_city}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Sub-city
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="applicant_sub_city"
                value={formState.applicant_sub_city}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Woreda
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="applicant_woreda"
                value={formState.applicant_woreda}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Kebele
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="applicant_kebele"
                value={formState.applicant_kebele}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                House Number
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="applicant_house_number"
                value={formState.applicant_house_number}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="applicant_phone_number"
                value={formState.applicant_phone_number}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value);
                  setFormState((prev) => ({
                    ...prev,
                    applicant_phone_number: formatted,
                  }));
                }}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Email
              </label>
              <input
                type="email"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="applicant_email"
                value={formState.applicant_email}
                onChange={handleChange}
              />
            </div>

            {/* Prosecution Details */}
            <div className="col-span-2 mt-2">
              <h3 className="font-medium text-gray-700 mb-2">
                Prosecution Details
              </h3>
            </div>

            <div className="flex flex-col col-span-2">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Reason for Private Prosecution *
              </label>
              <textarea
                rows={4}
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="reason_for_private_prosecution"
                value={formState.reason_for_private_prosecution}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* <div className="mt-4">
            <FileUpload
              label="Private Prsecution Decision"
              fileTypes="image/*, .pdf"
              info="Upload PDF or Image Files "
              // required={true}
              onChange={(attachment) =>
                setFormState((prev) => ({
                  ...prev,
                  private_prosecution_decision: attachment,
                }))
              }
            />
          </div> */}
          <div className="mt-4">
            <FileUpload
              label="Private Prsecution Decision"
              fileTypes="image/*, .pdf"
              info="Upload PDF or Image Files "
              // required={true}
              onChange={(attachment) =>
                setFormState((prev) => ({
                  ...prev,
                  private_prosecution_decision_file: attachment as any,
                }))
              }
            />
          </div>

          <div className="mt-6 flex justify-between gap-2">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded w-full hover:bg-gray-300"
              onClick={onCancel}
              type="button"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`border border-[#E90101] bg-[#E90101] px-10 py-2 text-white 
                        font-[Poppins] cursor-pointer font-[500] w-full
                        text-base rounded-md hover:bg-white hover:text-[#E90101]
                        ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestPrivateProsecutiion;
